import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import AccordionItem from "../general/AccordionItem/AccordionItem";
import Button from "../general/Button/Button";
import "./ShowcaseModal.css";

const ShowcaseModal = ({ format, onClose, onCopyUrl }) => {
  const overlayRef = useRef(null);
  const modalRef = useRef(null);
  const isClosingRef = useRef(false);

  useEffect(() => {
    if (!format) return;

    gsap.fromTo(
      overlayRef.current,
      { opacity: 0 },
      { opacity: 1, duration: 0.25, ease: "power2.out" }
    );
    gsap.fromTo(
      modalRef.current,
      { y: 20, scale: 0.96, opacity: 0 },
      { y: 0, scale: 1, opacity: 1, duration: 0.35, ease: "power3.out" }
    );

    const handleEsc = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [format]);

  const handleClose = () => {
    if (isClosingRef.current) return; 
    isClosingRef.current = true;

    gsap.to(modalRef.current, {
      y: 20,
      scale: 0.95,
      opacity: 0,
      duration: 0.25,
      ease: "power2.in",
    });

    gsap.to(overlayRef.current, {
      opacity: 0,
      duration: 0.25,
      ease: "power2.in",
      onComplete: () => {
        isClosingRef.current = false;
        onClose && onClose();
      },
    });
  };

  if (!format) return null;

  const previewUrl = format.preview?.toLowerCase();
  const isVideo = previewUrl?.endsWith(".mp4") || previewUrl?.endsWith(".webm");
  const isImage =
    previewUrl?.endsWith(".png") ||
    previewUrl?.endsWith(".jpg") ||
    previewUrl?.endsWith(".jpeg") ||
    previewUrl?.endsWith(".gif");

  return (
    <div
      ref={overlayRef}
      className="showcase-modal-overlay"
      onClick={handleClose}
    >
      <div
        ref={modalRef}
        className="showcase-modal"
        onClick={(e) => e.stopPropagation()}
      >

        <header className="showcase-modal-header">
          <div className="showcase-modal-header-info">
            <h2>{format.name}</h2>
            <span className="showcase-badge">{format.category}</span>
          </div>
          <span className="btn-close" onClick={handleClose}>
            ×
          </span>
        </header>

        <div className="showcase-modal-main">
          {format.preview && (
            <div className="showcase-modal-preview">
              {isVideo ? (
                <video
                  src={format.preview}
                  autoPlay
                  loop
                  muted
                  controls
                  playsInline
                  preload="metadata"
                />
              ) : isImage ? (
                <img src={format.preview} alt={format.name} />
              ) : (
                <p>Preview format not supported</p>
              )}
            </div>
          )}

          <div className="showcase-modal-body">
            <AccordionItem title="Technical Specifications" defaultOpen>
              {format.specs ? (
                Array.isArray(format.specs) ? (
                  <ul className="showcase-specs">
                    {format.specs.map((spec, i) => (
                      <li key={i}>{spec}</li>
                    ))}
                  </ul>
                ) : typeof format.specs === "object" ? (
                  <>
                    {format.specs.general?.length > 0 && (
                      <>
                        <h4>General</h4>
                        <ul className="showcase-specs">
                          {format.specs.general.map((spec, i) => (
                            <li key={`g-${i}`}>{spec}</li>
                          ))}
                        </ul>
                      </>
                    )}
                    {format.specs.technical?.length > 0 && (
                      <>
                        <h4>Technical</h4>
                        <ul className="showcase-specs">
                          {format.specs.technical.map((spec, i) => (
                            <li key={`t-${i}`}>{spec}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </>
                ) : (
                  <p>No specifications available.</p>
                )
              ) : (
                <p>No specifications available.</p>
              )}
            </AccordionItem>
          </div>
        </div>

        <footer className="showcase-modal-footer">
          {format.docUrl && (
            <>
              <Button
                variant="doc"
                onClick={() => window.open(format.docUrl, "_blank")}
              >
                Open Documentation
              </Button>
              <Button className="copy-url-btn" onClick={onCopyUrl}>
                <i className="fa-solid fa-link" /> Copy Documentation URL
              </Button>
            </>
          )}
          {format.platformPreviewLink && (
            <>
              <Button
                variant="preview"
                onClick={() =>
                  window.open(format.platformPreviewLink, "_blank")
                }
              >
                View Preview
              </Button>
              <Button className="copy-url-btn" onClick={onCopyUrl}>
                <i className="fa-solid fa-link" /> Copy Preview URL
              </Button>
            </>
          )}
          {!format.docUrl && !format.platformPreviewLink && (
            <p>No documentation or preview available</p>
          )}
        </footer>
      </div>
    </div>
  );
};

export default ShowcaseModal;
